import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  OTP_LENGTH,
  enterDigits,
  removeDigit,
  typedDigits,
} from '@client/shared/utils/otpUtils/otpUtils';
import styles from './otpInput.scss';

// A code entered one digit per box: typing moves on, backspace steps back, and
// pasting (or an autofilled one-time code) fills every box at once.
function OtpInput({
  value,
  onChange,
  onEnter,
  autoFocus,
  hasError,
  className,
}) {
  const boxes = useRef([]);

  function focusBox(index) {
    const box = boxes.current[index];

    if (box) {
      box.focus();
      box.select();
    }
  }

  // After React has put the new digits in the boxes, so the selection sticks.
  function focusBoxSoon(index) {
    setTimeout(() => focusBox(index), 0);
  }

  useEffect(() => {
    if (autoFocus) {
      focusBox(0);
    }
  }, [autoFocus]);

  function apply({ value: next, focusIndex }) {
    onChange(next);
    focusBoxSoon(focusIndex);
  }

  function handleChange(index, event) {
    apply(
      enterDigits(
        value,
        index,
        typedDigits(value[index] || '', event.target.value)
      )
    );
  }

  function handleKeyDown(index, event) {
    const hasModifier = event.metaKey || event.ctrlKey || event.altKey;

    if (event.key === 'Enter') {
      // Handled here, once, whether or not the code is complete yet.
      event.preventDefault();

      if (onEnter && value.length === OTP_LENGTH) {
        onEnter();
      }
    } else if (event.key === 'Backspace' && !hasModifier) {
      event.preventDefault();
      apply(removeDigit(value, index));
    } else if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      focusBox(index - 1);
    } else if (event.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      event.preventDefault();
      focusBox(index + 1);
    }
  }

  function handlePaste(index, event) {
    event.preventDefault();
    apply(enterDigits(value, index, event.clipboardData.getData('text')));
  }

  return (
    <div
      role="group"
      aria-label="Authentication code"
      className={classNames(styles.otpInput, className)}
    >
      {Array.from({ length: OTP_LENGTH }, (_, index) => (
        <input
          key={index}
          ref={(element) => {
            boxes.current[index] = element;
          }}
          className={classNames(styles.box, hasError && styles.boxError)}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          aria-label={`Digit ${index + 1} of ${OTP_LENGTH}`}
          value={value[index] || ''}
          onChange={(event) => handleChange(index, event)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={(event) => handlePaste(index, event)}
          onFocus={(event) => event.target.select()}
        />
      ))}
    </div>
  );
}

OtpInput.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onEnter: PropTypes.func,
  autoFocus: PropTypes.bool,
  hasError: PropTypes.bool,
  className: PropTypes.string,
};

export default OtpInput;
